import { useId, useState } from 'react';

import { cn } from '@shared/ui/cn';

import { Field, FIELD_CONTROL_CLASS } from '../_shared/components';
import type { ExternalCandidate } from '../_shared/types';
import { copy } from './copy';

/**
 * Assisted fill — query, candidates, one action.
 *
 * **The fill writes into the real inputs**, never into a parallel copy beside
 * them, which is what keeps every filled field editable afterwards. That is the
 * whole point of the feature: the provider is an accelerator and the operator
 * corrects what it got wrong.
 *
 * **Enrichment is never a dependency for publication.** When the lookup fails
 * the panel says so and the form beside it stays completely usable by hand — the
 * failure notice sits in the panel rather than over the page.
 *
 * **The search button is secondary.** The page keeps one primary button and it
 * is «احفظ العنوان»; filling is a step on the way there rather than an end, and
 * two primary buttons on one screen leave the operator asking which finishes the
 * task.
 *
 * The candidate list is a single-select radio group whose **whole row is the
 * target**, so a pointer does not have to find a 16-pixel circle.
 *
 * The reference link is a **fact, not a field**. Both public surfaces build it
 * from this identifier and print the identifier as the link's own text, so a
 * hand-editable URL here would open two sources of truth for one value with no
 * way to tell which is lying.
 */
export function ProviderPanel({
  provider,
  query,
  candidates,
  selected,
  busy,
  applying,
  notice,
  appliedNote,
  boundExternalId,
  referenceUrl,
  onQuery,
  onSearch,
  onSelect,
  onApply,
}: {
  /** Read from the owning service — the binding is configuration, not a literal. */
  provider: string;
  query: string;
  candidates: readonly ExternalCandidate[] | null;
  selected: string | null;
  busy: boolean;
  applying: boolean;
  /** A refusal or a provider outage, stated in the panel rather than over the page. */
  notice: string | null;
  appliedNote: string | null;
  boundExternalId: string | null;
  referenceUrl: string | null;
  onQuery: (next: string) => void;
  onSearch: () => void;
  onSelect: (externalId: string) => void;
  onApply: () => void;
}) {
  const groupName = useId();
  const [dirty, setDirty] = useState(false);

  return (
    /*
      **Two columns from `--bp-xl`: what is asked on one side, what came back on
      the other.** The panel spans an eleven-hundred-pixel authoring column and
      everything in it was capped at the measure, so a card the width of the
      form carried a search box a quarter of that and a bare band beside it. The
      split also puts the candidate rows level with the query that produced
      them rather than a scroll below it.
    */
    <section
      aria-labelledby="provider-panel"
      className="grid gap-4 rounded-md border border-border bg-surface p-4 xl:grid-cols-2 xl:items-start xl:gap-x-8"
    >
      <h2 id="provider-panel" className="font-display text-h3 font-bold xl:col-span-2">
        {copy.provider.heading}
      </h2>

      <div className="grid content-start gap-4">
        <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.provider.lead(provider)}</p>

        <Field label={copy.provider.queryLabel} hint={copy.provider.queryHint} wide>
          {(id, describedBy) => (
            <div className="flex flex-wrap items-center gap-2">
              <input
                id={id}
                type="text"
                dir="ltr"
                value={query}
                onChange={(event) => {
                  setDirty(true);
                  onQuery(event.target.value);
                }}
                aria-describedby={describedBy}
                className={cn(FIELD_CONTROL_CLASS, 'mono min-w-[12rem] flex-1')}
              />
              <button
                type="button"
                disabled={busy || query.trim() === ''}
                onClick={onSearch}
                className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
              >
                {busy ? copy.provider.searching : copy.provider.search}
              </button>
            </div>
          )}
        </Field>

        {/* Said before the press, not discovered after it. */}
        <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.provider.savesDraftFirst}</p>
      </div>

      <div className="grid content-start gap-4">
        {notice ? (
          <p role="status" className="mb-0 max-w-measure text-sm text-ink">
            {notice}
          </p>
        ) : null}

        {candidates && candidates.length === 0 && dirty ? (
          <p className="mb-0 max-w-measure text-sm text-ink-muted">{copy.provider.noResults}</p>
        ) : null}

        {candidates && candidates.length > 0 ? (
          <div className="grid gap-2">
            <p className="mb-0 max-w-none text-xs font-semibold text-ink">{copy.provider.resultsCaption}</p>
            <ul className="grid list-none gap-2 p-0">
              {candidates.map((candidate) => (
                <li key={candidate.external_id}>
                  {/* The whole row is the target, not the radio's own circle. */}
                  <label
                    htmlFor={`${groupName}-${candidate.external_id}`}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-sm border p-3',
                      selected === candidate.external_id
                        ? 'border-accent bg-accent-soft'
                        : 'border-border bg-surface-raised hover:bg-hover-bg',
                    )}
                  >
                    <input
                      id={`${groupName}-${candidate.external_id}`}
                      type="radio"
                      name={groupName}
                      value={candidate.external_id}
                      checked={selected === candidate.external_id}
                      onChange={() => onSelect(candidate.external_id)}
                      className="mt-1"
                    />
                    <span className="text-sm text-ink">
                      <bdi>{candidate.title}</bdi>
                    </span>
                    <span className="text-xs text-ink-muted">
                      <bdi className="mono">{candidate.external_id}</bdi>
                      {candidate.year === null ? null : <> · {candidate.year}</>}
                      {candidate.producing_country === null ? null : <> · {candidate.producing_country}</>}
                      {/*
                      A kind the mapper disagrees with is said, not hidden. The
                      row stays selectable because the operator decides — a
                      documentary the provider calls a series is still the right
                      match for a film record.
                    */}
                      {candidate.kind_mismatch ? (
                        <>
                          {' '}
                          · <span className="text-warning">{copy.provider.kindMismatch}</span>
                        </>
                      ) : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {boundExternalId && referenceUrl ? (
          <p className="mb-0 max-w-measure text-xs text-ink-muted">
            <span className="block">{copy.provider.referenceLead}</span>
            <a
              href={referenceUrl}
              rel="noopener noreferrer nofollow"
              target="_blank"
              className="text-accent-text"
            >
              {copy.provider.referenceText(provider, boundExternalId)}
            </a>
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={applying || selected === null}
            onClick={onApply}
            className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
          >
            {applying ? copy.provider.applying : copy.provider.apply}
          </button>
          <span className="max-w-measure text-xs text-ink-muted">{copy.provider.applyNote}</span>
        </div>

        {appliedNote ? (
          <p role="status" className="mb-0 max-w-measure text-sm text-ink">
            {appliedNote}
          </p>
        ) : null}
      </div>
    </section>
  );
}
